// Marco de un sitio público: encabezado (logo, nombre, miembro y «Salir») y
// las páginas como pestañas. En el celular la barra de pestañas se desplaza
// de lado y queda fija arriba al bajar. No depende del router: recibe el
// enlace de cada página y qué hacer al tocarla.

import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SiteShellProps {
  siteName: string;
  logo?: string;
  memberLabel?: string;
  pages: Array<{ key: string; title: string }>;
  current?: string;
  hrefFor: (page: string) => string;
  onNavigate: (page: string) => void;
  onLogout?: () => void;
  /** Dentro del editor de sitios: sin alto de pantalla completa ni pestañas fijas. */
  embedded?: boolean;
  children: ReactNode;
}

export function SiteShell({
  siteName,
  logo,
  memberLabel,
  pages,
  current,
  hrefFor,
  onNavigate,
  onLogout,
  embedded,
  children,
}: SiteShellProps) {
  const activeRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [current]);

  const go = (page: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onNavigate(page);
  };

  return (
    <div className={cn("flex flex-col", embedded ? "min-h-full" : "min-h-[100dvh]")}>
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          {logo ? (
            <img src={logo} alt="" className="h-8 w-auto max-w-[120px] shrink-0 object-contain" />
          ) : (
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-semibold text-primary-foreground">
              {siteName.trim().charAt(0).toUpperCase() || "S"}
            </div>
          )}
          <p className="min-w-0 flex-1 truncate font-semibold text-foreground">{siteName}</p>
          {memberLabel && (
            <span className="hidden max-w-[40%] truncate text-sm text-muted-foreground @min-[40rem]:inline" data-testid="site-member">
              {memberLabel}
            </span>
          )}
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <LogOut className="size-4" aria-hidden />
              Salir
            </button>
          )}
        </div>
      </header>

      {pages.length > 1 && (
        <nav
          className={cn(
            "border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80",
            !embedded && "sticky top-0 z-10",
          )}
          aria-label="Páginas"
        >
          <div className="site-scroll-x mx-auto flex max-w-5xl gap-1 overflow-x-auto px-2 @min-[40rem]:px-4">
            {pages.map((p) => {
              const active = p.key === current;
              return (
                <a
                  key={p.key}
                  ref={active ? activeRef : undefined}
                  href={hrefFor(p.key)}
                  onClick={go(p.key)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "relative shrink-0 whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors",
                    "after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full",
                    active
                      ? "text-primary after:bg-primary"
                      : "text-muted-foreground hover:text-foreground after:bg-transparent",
                  )}
                >
                  {p.title}
                </a>
              );
            })}
          </div>
        </nav>
      )}

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-5 @min-[40rem]:py-8">
        {memberLabel && (
          <p className="mb-4 truncate text-sm text-muted-foreground @min-[40rem]:hidden">
            Hola, <span className="font-medium text-foreground">{memberLabel}</span>
          </p>
        )}
        {children}
      </main>
    </div>
  );
}
